import React, {Fragment} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import cs from 'classnames';
import moment from 'moment-tz';
import {DefaultRootState, useSelector} from 'react-redux';
import {WithRouterProps} from 'react-router';
import styled from 'styled-components';
import {
  getProcedureTaskCore,
  getProcessTaskCore,
  ProcessTaskCoreWithVersion,
} from 'sweet-taskinfo';

import {loadComments} from 'app/comments/actions';
import {UserAvatar} from 'app/components/general/avatars';
import {ListMessage} from 'app/components/general/ListMessage';
import {CurrentUserSelector} from 'app/current-user/api';
import {ViewStepsModeTitle} from 'app/general/detailpage/ViewStepsModeTitle';
import {DetailLoadingPage} from 'app/general/loading/DetailLoadingPage';
import {showTaskUpgradeAvailableAlertBoxIfAvailable} from 'app/general/structures/document-alerts/TaskUpgradeAvailableAlertBox';
import {SPPage} from 'app/general/structures/SPPage';
import {withLoadingIndicator} from 'app/general/sweet-loading-bar';
import {DocumentPresenceMembers} from 'app/presence/components/DocumentPresenceMembers';
import {PresenceSubscriber} from 'app/presence/components/PresenceSubscriber';
import {CollapsedStepsState} from 'app/procedures/components/CollapsedStepsState';
import {ProcedureStep} from 'app/procedures/components/ProcedureDetailPage/ProcedureStep';
import {
  ProcedureDiagramMobile,
  ProcedureDiagramSidebar,
  ToggleProcedureDiagramMobileButton,
} from 'app/procedures/components/ProcedureDiagram';
import {StyledStepsListDIV} from 'app/procedures/components/structures';
import {normalizeSteps} from 'app/procedures/utils';
import {setProcessSideDiagramState} from 'app/process/actions';
import EmptyProcessList from 'app/process/components/EmptyProcessList.jsx';
import {ViewProcessMetadata} from 'app/process/components/ViewProcessMetadata';
import {LoadByHashidInjectedProps, loadProcessByHashid} from 'app/process/hocs';
import {getMemoizedProcessOrphanSteps} from 'app/process/selectors';
import {useAccountSettings} from 'app/settings/api';
import {StepFooter} from 'app/steps/components/StepFooter';
import {scrollIntoStep} from 'app/steps/utils';
import {useModelChangeSubscription} from 'app/subscriptions';
import {useTaskInstance} from 'app/tasks/api';
import {CheckableChoices} from 'app/tasks/components/ProcedureTaskPage/CheckableChoices';
import {TaskStepActionButtons} from 'app/tasks/components/ProcedureTaskPage/TaskStepActionButtons';
import {ViewTaskMetaDataExtra} from 'app/tasks/components/ProcedureTaskPage/ViewTaskMetaData/ViewTaskMetaDataExtra';
import {TaskDetailPageSidebar} from 'app/tasks/components/TaskDetailPageSidebar';
import {useFetchTaskLanes} from 'app/tasks/task-lanes';
import {
  useScrollIntoActiveStep,
  useScrollIntoStepInHashOnMount,
} from 'app/tasks/utils';
import {showToastNotification} from 'app/toasts/slice';
import {procedure_process_task_url, TASKS_HOME} from 'app/urls';
import {getProcessName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';
import {whenVisible} from 'app/utils/page-visibility';
import {useAsyncActions} from 'app/utils/redux';

import {ProcedureStepProgressBar} from './ProcedureStepProgressBar';
import {MarkCompletedButton} from './ProcedureTaskPage/MarkCompletedButton';
import {TaskPageActionButtons} from './ProcedureTaskPage/TaskPageActionButtons';
import {StepAssignmentAvatars} from './StepAssignmentAvatars';

import {useTaskUpdate} from '../task-update-hook';

import {
  AnyTaskStepType,
  AuthenticatedUserSerializedType,
  ProcessHashidSerializedType,
  TaskConcreteInstanceSerializedType,
  TaskInstanceProcessAssignedVersionContent,
  UpdateTaskPayloadType,
} from 'sweet-types';

import type {Map} from 'immutable';

interface PageParams {
  hashid: string;
  taskid: string;
}

interface Props
  extends WithRouterProps<PageParams>,
    LoadByHashidInjectedProps<ProcessHashidSerializedType> {
  route: {
    props: {
      showComments?: boolean;
      showActivity?: boolean;
      showTasks?: boolean;
    };
  };
}

export const ProcessTaskPage = loadProcessByHashid(
  ({
    params,
    router,
    route,
    fetchedByHashid: {object: process},
  }: Props): JSX.Element => {
    const {data: task, refetch, error} = useTaskInstance(params.taskid);
    const {updateTaskInstance} = useTaskUpdate();
    const {data: settings} = useAccountSettings(undefined, {
      refetchOnMountOrArgChange: 900,
    });

    const taskCore = React.useMemo(
      () => (task != null ? getProcessTaskCore(task) : null),
      [task]
    );

    const {timezone, closedSideDiagram} = useSelector((state) =>
      processTaskDataSelector(state, {params, processId: process.id})
    );

    const actions = useAsyncActions({
      showToastNotification,
      withLoadingIndicator,
      loadComments,
      setProcessSideDiagramState,
    });

    useFetchTaskLanes(taskCore);

    const taskDeleted = React.useMemo(() => {
      return error != null && 'status' in error && error.status === 404;
    }, [error]);

    const [showOverlayDiagram, setShowOverlayDiagram] = React.useState(false);
    const toggleDiagramOverlay = () => setShowOverlayDiagram((show) => !show);

    const updatingTask = React.useRef(false);

    React.useEffect(() => {
      actions.loadComments(parseInt(params.taskid), 'taskinstance');
      fetchTaskinstanceData();
    }, [params.taskid]);

    useScrollIntoStepInHashOnMount(taskCore);

    React.useEffect(() => {
      if (taskDeleted && taskCore == null) {
        actions.showToastNotification(gt('Task not found'), {
          level: 'error',
        });
        router.push(TASKS_HOME);
      }
    }, [taskDeleted]);

    const taskName = taskCore?.task.description || taskCore?.task.name || '';
    const pageTitle = React.useMemo(() => {
      if (taskCore?.task != null) {
        const due = moment(taskCore.task.due).tz(timezone).format('lll');
        return gt('%(name)s | Task | %(due)s', {
          name: taskName,
          due,
        });
      }
      return '';
    }, [taskCore?.task.due, timezone, taskName]);

    useModelChangeSubscription(
      (message, {runWhenAny, modelIdMatch, allRelatedMatch}) => {
        if (taskCore?.task != null) {
          const taskInstanceId = parseInt(params.taskid);
          const {task} = taskCore;

          runWhenAny(
            message,
            [
              modelIdMatch('taskinstance', taskInstanceId),
              modelIdMatch('tasktemplate', task.template.id),
            ],
            () => (!updatingTask.current ? fetchTaskinstanceData() : null)
          );

          runWhenAny(
            message,
            [
              allRelatedMatch('comment', {
                content_type: 'taskinstance',
                id: taskInstanceId,
              }),
            ],
            () => actions.loadComments(taskInstanceId, 'taskinstance')
          );
        }
      },
      [taskCore?.task?.id]
    );

    const normalizedSteps = React.useMemo(
      () =>
        normalizeSteps(taskCore?.hasVersion ? taskCore.version.content : []),
      [taskCore?.hasVersion ? taskCore.version?.content : null]
    );

    useScrollIntoActiveStep(taskCore);

    const fetchTaskinstanceData = whenVisible(() => refetch());

    function updateTask(
      task: TaskConcreteInstanceSerializedType,
      update: UpdateTaskPayloadType
    ) {
      updatingTask.current = true;
      return updateTaskInstance({url: task.url, update}).then(
        (task) => {
          updatingTask.current = false;
          return task;
        },
        (error) => {
          updatingTask.current = false;
          throw error;
        }
      );
    }

    function setStepCompleted(
      taskCore: ProcessTaskCoreWithVersion,
      step: AnyTaskStepType,
      completed: boolean,
      choice?: string | null
    ) {
      const payload =
        step.type === 'processprocedure'
          ? taskCore.payload.toSetProcedureCompleted(step.id, completed)
          : taskCore.payload.toSetDecisionStepCompleted(
              step.id,
              completed ? choice ?? null : null
            );

      return updateTask(
        taskCore.task as TaskConcreteInstanceSerializedType,
        payload
      ).then(() => {
        actions.showToastNotification(
          completed ? gt('Marked as complete.') : gt('Marked as incomplete.')
        );
      });
    }

    if (!taskCore || task == null) {
      return <DetailLoadingPage />;
    }

    const versionContent: TaskInstanceProcessAssignedVersionContent =
      taskCore.hasVersion
        ? taskCore.version
        : {
            hashid: '',
            content_type: 'processversion',
            description: '',
            procedures: [],
            name: taskCore.titles[0],
            content: [],
            steps_blocked_at: null,
            id: 0,
            lanes: {},
          };

    const orphans = getMemoizedProcessOrphanSteps(
      versionContent.content,
      versionContent.steps_blocked_at
    );

    return (
      <SPPage
        pageTitle={pageTitle}
        breadcrumbProps={{docName: getProcessName(taskName)}}
        horizontalPadding={false}
        actionArea={
          <TaskPageActionButtons
            taskCore={taskCore}
            object={process}
            stepsLength={
              taskCore.hasVersion === true ? taskCore.version.content.length : 0
            }
          />
        }
        sidebar={
          <TaskDetailPageSidebar
            objectContentType="proceduregroup"
            object={process}
            task={task}
            router={router}
            route={route}
          />
        }
        alternativeOverlayContent={
          taskCore.hasVersion && showOverlayDiagram ? (
            <ProcedureDiagramMobile
              toggleDiagram={toggleDiagramOverlay}
              steps={normalizedSteps}
            />
          ) : null
        }
      >
        <DocumentPresenceMembers
          contentType="taskinstance"
          objectId={task.id}
          pageContext="view"
          containerClass={cs('col-12', {
            'col-md-8': !closedSideDiagram,
          })}
        />
        <PresenceSubscriber
          contentType={task.template.assigned_object.content_type}
          objectsIds={[task.template.assigned_object.id]}
          pageContext={'task'}
          additionalData={{
            contentType: 'taskinstance',
            objectId: task.id,
            html_url: task.html_url,
            name: taskName,
            description: task.description,
          }}
        />
        <ViewProcessMetadata
          task={task}
          process={process}
          policies={process.policies}
          metaContentType="taskinstance"
          tags={process.tags}
          content={versionContent}
          warningComponent={
            <Fragment>
              {showTaskUpgradeAvailableAlertBoxIfAvailable(task, taskDeleted)}
            </Fragment>
          }
          extraContent={
            <ViewTaskMetaDataExtra
              taskCore={taskCore}
              setWarnIncomplete={() => null}
              hasDescription={versionContent.description != null}
              updateTaskInstance={updateTask}
            />
          }
        />

        <CollapsedStepsState
          stepsIds={
            taskCore.hasVersion ? taskCore.version.content.map((s) => s.id) : []
          }
        >
          {({
            collapsedSteps,
            numCollapsedSteps,
            toggleAllStepsCollapse,
            toggleStepCollapse,
          }) => (
            <StyledStepsListDIV className="js-document-body container pt-0">
              <div className="row">
                <div
                  className={cs('col', 'pt-4', 'col-12', {
                    'col-md-8': !closedSideDiagram && taskCore.hasVersion,
                  })}
                >
                  <ViewStepsModeTitle
                    title={gt('Procedures List')}
                    numCollapsedSteps={numCollapsedSteps}
                    toggleAllStepsCollapse={toggleAllStepsCollapse}
                  />
                  {taskCore.hasVersion ? (
                    taskCore.version.content.length > 0 ? (
                      <ol className="steps mt-4">
                        {taskCore.state.contentWithProgress.map(
                          ([step, progress, stepAssignments]) => {
                            const {
                              isActive,
                              isCompleted,
                              canToggle,
                              completed,
                            } = progress;
                            const isCollapsed = collapsedSteps[step.id];

                            const showToggle: boolean =
                              step.type === 'processprocedure' ||
                              (step.type === 'decision' && isCompleted);

                            const patchedStep =
                              step.type === 'processprocedure' && !step.deleted
                                ? {
                                    ...step,
                                    html_url: procedure_process_task_url({
                                      object: step,
                                      process,
                                      task: taskCore.task as TaskConcreteInstanceSerializedType,
                                    }),
                                  }
                                : step;

                            let isProcedureStepsBlocked = false;

                            if (
                              !isCompleted &&
                              step.type === 'processprocedure' &&
                              !step.deleted
                            ) {
                              const procedureTaskCore = getProcedureTaskCore(
                                task,
                                step.id
                              );
                              if (procedureTaskCore.hasVersion) {
                                const {canBeCompleted, isSequenced} =
                                  procedureTaskCore.state.progressInfo;
                                if (isSequenced && !canBeCompleted) {
                                  isProcedureStepsBlocked = true;
                                }
                              }
                            }

                            return (
                              <StyledProcedureStepLi
                                key={step.id}
                                id={step.id}
                                blocked={!canToggle}
                              >
                                <ProcedureStep
                                  index={step.n}
                                  step={patchedStep}
                                  orphan={orphans.has(step.id)}
                                  showCopyLink={false}
                                  collapsed={
                                    isCollapsed != null
                                      ? isCollapsed
                                      : !isActive || isCompleted
                                  }
                                  mediaURL={settings?.MEDIA_URL}
                                  mediaURLRead={settings?.MEDIA_URL_READ}
                                  onToggleCollapse={toggleStepCollapse}
                                  actionButtons={
                                    showToggle ? (
                                      <TaskStepActionButtons
                                        isCompleted={isCompleted}
                                        completedBy={
                                          completed != null
                                            ? completed.by
                                            : null
                                        }
                                        completedAt={
                                          completed != null
                                            ? completed.at
                                            : null
                                        }
                                        disabled={!canToggle}
                                        isProcedureStepsBlocked={
                                          isProcedureStepsBlocked
                                        }
                                        userTimezone={timezone}
                                        onToggleCheck={(checked) =>
                                          setStepCompleted(
                                            taskCore,
                                            step,
                                            checked,
                                            null
                                          )
                                        }
                                      />
                                    ) : null
                                  }
                                  choicesList={
                                    step.type === 'decision' ? (
                                      <CheckableChoices
                                        disabled={!canToggle}
                                        onSetStepChoice={(choice, checked) =>
                                          setStepCompleted(
                                            taskCore,
                                            step,
                                            checked,
                                            choice.id
                                          )
                                        }
                                        choices={step.choices}
                                        steps={taskCore.version.content}
                                        pickedChoice={
                                          completed != null
                                            ? completed.pickedChoice
                                            : null
                                        }
                                        userIndicator={
                                          progress.completed &&
                                          progress.completed.by ? (
                                            <UserAvatar
                                              className="mr-3"
                                              small={true}
                                              user={progress.completed.by}
                                              tooltipTitle={(userName) =>
                                                gt(
                                                  'Answer picked by %s on %s',
                                                  <b>{userName}</b>,
                                                  moment(progress.completed?.at)
                                                    .tz(timezone)
                                                    .format('ll')
                                                )
                                              }
                                            />
                                          ) : null
                                        }
                                      />
                                    ) : null
                                  }
                                  next={
                                    <StepFooter
                                      step={step}
                                      steps={taskCore.version.content}
                                      stepLaneId={
                                        'lanes' in taskCore.version
                                          ? taskCore.version.lanes[step.id]
                                          : null
                                      }
                                    />
                                  }
                                  previous={
                                    <StepAssignmentAvatars
                                      stepAssignments={stepAssignments}
                                    />
                                  }
                                />
                                {step.type === 'processprocedure' &&
                                !step.deleted ? (
                                  <ProcedureStepProgressBar
                                    isCompleted={isCompleted}
                                    task={task}
                                    stepId={step.id}
                                  />
                                ) : null}
                              </StyledProcedureStepLi>
                            );
                          }
                        )}
                      </ol>
                    ) : (
                      <EmptyProcessList
                        processHTMLURL={process.html_url}
                        canChange={process.can_change}
                      />
                    )
                  ) : (
                    <ListMessage gray={true}>
                      {gt(
                        'Process version attached to this task has been deleted'
                      )}
                    </ListMessage>
                  )}
                  <div className="clearfix">
                    <MarkCompletedButton
                      taskCore={taskCore}
                      updateTaskInstance={updateTask}
                      onBlockedClick={() => null}
                      className="float-right"
                    />
                  </div>
                </div>
                {!showOverlayDiagram && taskCore.hasVersion ? (
                  <ProcedureDiagramSidebar
                    onStepClick={scrollIntoStep}
                    className="col-4 mt-4"
                    showingSidebarDiagram={!closedSideDiagram}
                    toggleSidebarDiagram={() =>
                      actions.setProcessSideDiagramState(
                        process.id,
                        !closedSideDiagram
                      )
                    }
                    toggleOverlayDiagram={toggleDiagramOverlay}
                    versionId={process.current_version.id}
                    versionContentType="processversion"
                    steps={normalizedSteps}
                  />
                ) : null}
              </div>
              <ToggleProcedureDiagramMobileButton
                showingDiagram={false}
                toggleDiagram={toggleDiagramOverlay}
              />
            </StyledStepsListDIV>
          )}
        </CollapsedStepsState>
      </SPPage>
    );
  }
);

const processTaskDataSelector = createSelector<
  [
    (
      state: DefaultRootState,
      extra: {params: PageParams; processId: number}
    ) => number,
    (state: DefaultRootState) => AuthenticatedUserSerializedType,
    (state: DefaultRootState) => Map<string, boolean>
  ],
  {
    timezone: string;
    closedSideDiagram: boolean;
  }
>(
  [
    (state, {processId}) => processId,
    (state) => CurrentUserSelector(state).data!,
    (state) => state.processArea.get('closedSideDiagrams', false),
  ],
  (processId, {timezone, is_diagrams_expanded}, closedSideDiagrams) => {
    return {
      timezone,
      closedSideDiagram: closedSideDiagrams.get(
        processId.toString(),
        !is_diagrams_expanded
      ),
    };
  }
);

const StyledProcedureStepLi = styled.li<{blocked?: boolean}>`
  opacity: ${(props) => (props.blocked ? 0.4 : null)};
`;
